import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertCircle, ArrowLeft, Award, CircleAlert, CreditCard, Crown, Download, IndianRupee, Info, Loader2, Package, PackageOpen,
    Receipt, RefreshCw, Scissors, Settings2, TrendingUp, User, UserX, Users,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import styles from './StaffAnalytics.module.css';
import { useStore } from '../../../context/StoreContext';
import { API_ROOT as API_BASE } from '../../../config/api';

// =============================================================================
// Staff analytics: who sold what, memberships by staff, sales without staff.
// Every bill is counted on the day it was completed (same as Dashboard).
// Services paid by a PREPAID CARD are credited at what the salon earned for them:
// a ₹10,000 card with ₹15,000 credit gives ₹1,000 staff credit for a ₹1,500 service.
// Bills and sales totals keep the real price; only staff credit (and incentive) changes.
// =============================================================================

// Purple / green / pink: no yellow anywhere
const KIND_META = {
    services: { label: 'Services', color: 'var(--h-color-primary-600, #673de6)' },
    products: { label: 'Products', color: 'var(--h-color-success-500, #009e5b)' },
    memberships: { label: 'Memberships', color: 'var(--danger--500, #fc5185)' },
};
const KINDS = Object.keys(KIND_META);
// Services given from client packages (paid earlier): staff credit, not shop revenue
const PKG_COLOR = 'var(--h-color-primary-300, #a48df2)';
const pkgOf = (s) => s?.packageServices || { count: 0, value: 0, items: [] };
const creditOf = (s) => (s?.credit ?? ((s?.gross || 0) + (pkgOf(s).value || 0)));
// Prepaid card services of a staff: billed at bill price, credited at card value
const cardOf = (s) => s?.prepaid || { billed: 0, credited: 0, adjust: 0, lines: 0, ratio: 1 };
const CARD_COLOR = 'var(--danger--500, #fc5185)';
const ratePct = (ratio) => `${Math.round((Number(ratio) || 1) * 100)}%`;
// Route of the salon settings page (Settings > Staff defaults holds the prepaid card rule)
const SETTINGS_PATH = '/settings';
/** Explains the rule the server used for prepaid card services (Settings > Staff defaults) */
const policyNote = (policy, avgRatio) => {
    if (!policy || policy.mode === 'CARD_RATE') {
        return { title: "Counted at each card's real rate", text: <>Prepaid cards give more service than the client paid (for example ₹15,000 of services for ₹10,000). Staff credit for card services counts what the salon earned: <b>bill amount × card price ÷ card credit</b>. On average this period that is <b>{ratePct(avgRatio)}</b> of the bill.</> };
    }
    if (policy.mode === 'FIXED_PERCENT') {
        return { title: `Counted at ${policy.setting?.percent}% of the bill`, text: <>Staff credit for card services counts a fixed <b>{policy.setting?.percent}%</b> of the bill price, as set in Settings.</> };
    }
    return { title: 'Counted at the full bill price', text: <>Card services count at the full bill price for staff{policy.why ? <> ({policy.why})</> : null}. Turn on "Count card services at card value" in Settings to credit only what the salon earned.</> };
};
const COLOR_STAFF = 'var(--h-color-primary-600, #673de6)';
const COLOR_NO_STAFF = 'var(--h-color-neutral-400, #9d9ea4)';

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 });
const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);
const shortInr = (v) => {
    const n = Number(v || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
    return `₹${Math.round(n)}`;
};
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '—');
const fmtDay = (ymd) => {
    const [y, m, d] = String(ymd).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};
const initials = (name) => {
    const parts = String(name || '').trim().split(/\s+/);
    return (parts.length > 1 ? parts[0][0] + parts[1][0] : String(name || 'ST').slice(0, 2)).toUpperCase();
};
/** Round the chart top up to a clean number */
const niceMax = (v) => {
    if (v <= 0) return 100;
    const p = 10 ** Math.floor(Math.log10(v));
    return [1, 2, 2.5, 5, 10].map((m) => m * p).find((m) => m >= v);
};
const zeroStaff = (s, days) => ({
    id: s.id, staffId: s.id, name: s.name,
    services: { count: 0, amount: 0 }, products: { count: 0, amount: 0 }, memberships: { count: 0, amount: 0 },
    net: 0, gst: 0, gross: 0, bills: 0, clients: 0, consultations: 0, avgTicket: 0, share: 0,
    trend: new Array(days).fill(0), topServices: [], membershipSales: [], log: [],
    packageServices: { count: 0, value: 0, items: [] }, packageSales: [], packagesSold: { count: 0, amount: 0 }, credit: 0, creditShare: 0,
    creditSales: 0, prepaid: { billed: 0, credited: 0, adjust: 0, lines: 0, ratio: 1 },
});

/* ============================== charts ============================== */
const TrendChart = ({ points, series }) => {
    const [hover, setHover] = useState(null);
    const totals = points.map((p) => series.reduce((s, k) => s + (p[k.key] || 0), 0));
    const top = niceMax(Math.max(0, ...totals));
    const labelEvery = Math.max(1, Math.ceil(points.length / 8));
    const hovered = hover !== null ? points[hover] : null;

    return (
        <div className={styles.chart}>
            <div className={styles.chartArea}>
                <div className={styles.yAxis} aria-hidden="true">
                    {[1, 0.75, 0.5, 0.25, 0].map((f) => <span key={f}>{shortInr(top * f)}</span>)}
                </div>
                <div className={styles.plot} onMouseLeave={() => setHover(null)}>
                    {[0, 25, 50, 75].map((g) => <i key={g} className={styles.gridLine} style={{ bottom: `${g}%` }} />)}
                    <div className={styles.bars} style={{ gap: points.length > 40 ? 1 : points.length > 20 ? 2 : 6 }}>
                        {points.map((p, i) => (
                            <button
                                type="button"
                                key={p.date}
                                className={`${styles.barCol} ${hover === i ? styles.barColActive : ''}`}
                                onMouseEnter={() => setHover(i)}
                                onFocus={() => setHover(i)}
                                onBlur={() => setHover(null)}
                                aria-label={`${fmtDay(p.date)}: ${series.map((k) => `${k.label} ${inr(p[k.key])}`).join(', ')}`}
                            >
                                <span className={styles.barStack} style={{ height: `${pct(totals[i], top)}%` }}>
                                    {series.map((k) => (p[k.key] > 0 ? <span key={k.key} style={{ flexGrow: p[k.key], background: k.color }} /> : null))}
                                </span>
                            </button>
                        ))}
                    </div>
                    {hovered && (
                        <div className={styles.tooltip} style={{ left: `${((hover + 0.5) / points.length) * 100}%` }} role="status">
                            <strong>{fmtDay(hovered.date)}</strong>
                            {series.map((k) => (
                                <span key={k.key}><i style={{ background: k.color }} /> {k.label}<b>{inr(hovered[k.key])}</b></span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.xAxis} aria-hidden="true">
                {points.map((p, i) => <span key={p.date}>{i % labelEvery === 0 ? fmtDay(p.date) : ''}</span>)}
            </div>
            <div className={styles.legend}>
                {series.map((k) => <span key={k.key}><i style={{ background: k.color }} /> {k.label}</span>)}
            </div>
        </div>
    );
};

const Donut = ({ parts, centerLabel, centerValue }) => {
    const total = parts.reduce((s, p) => s + p.value, 0);
    const R = 42;
    const C = 2 * Math.PI * R;
    let offset = 0;
    return (
        <div className={styles.donutWrap}>
            <svg viewBox="0 0 100 100" className={styles.donut} role="img" aria-label={`${centerLabel} ${centerValue}`}>
                <circle cx="50" cy="50" r={R} fill="none" stroke="var(--h-color-neutral-100, #f5f5f6)" strokeWidth="12" />
                {total > 0 && parts.map((p) => {
                    const len = (p.value / total) * C;
                    const el = (
                        <circle
                            key={p.label}
                            cx="50" cy="50" r={R} fill="none"
                            stroke={p.color} strokeWidth="12"
                            strokeDasharray={`${len} ${C - len}`}
                            strokeDashoffset={-offset}
                            transform="rotate(-90 50 50)"
                        />
                    );
                    offset += len;
                    return el;
                })}
                <text x="50" y="46" textAnchor="middle" className={styles.donutLabel}>{centerLabel}</text>
                <text x="50" y="60" textAnchor="middle" className={styles.donutValue}>{centerValue}</text>
            </svg>
            <ul className={styles.donutLegend}>
                {parts.map((p) => (
                    <li key={p.label}>
                        <i style={{ background: p.color }} />
                        <span>{p.label}</span>
                        <strong>{inr(p.value)}</strong>
                        <em>{Math.round(pct(p.value, total))}%</em>
                    </li>
                ))}
            </ul>
        </div>
    );
};

/* ============================== page ============================== */
const StaffAnalytics = ({ dateFilter }) => {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const storeId = activeStore?.id || activeStore?._id;
    const token = localStorage.getItem('smiify_token');

    const [staffList, setStaffList] = useState([]);
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);
    const [view, setView] = useState('overview'); // overview | unassigned | <staffId>

    /* ----------------------------- staff list ----------------------------- */
    useEffect(() => {
        if (!storeId) return undefined;
        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/api/v1/smiify/sm/staff?storeId=${storeId}`, { headers: { Authorization: `Bearer ${token}` } });
                const json = await res.json().catch(() => ({}));
                if (alive && json.success) {
                    setStaffList((json.data || [])
                        .filter((s) => !s.status || s.status === 'Active')
                        .map((s) => ({ id: String(s.id || s._id), name: s.name, role: s.role || s.designation || '' })));
                }
            } catch { /* optional */ }
        })();
        return () => { alive = false; };
    }, [storeId, token]);

    /* ----------------------------- analytics ----------------------------- */
    useEffect(() => {
        if (!storeId || !dateFilter?.startDate) return undefined;
        const controller = new AbortController();
        (async () => {
            setIsLoading(true);
            setError('');
            try {
                const params = new URLSearchParams({
                    storeId: String(storeId),
                    startDate: dateFilter.startDate,
                    endDate: dateFilter.endDate,
                    period: dateFilter.value || 'today',
                    tzOffset: String(new Date().getTimezoneOffset()),
                });
                const res = await fetch(`${API_BASE}/api/v1/smiify/sm/analytics/staff-analytics?${params}`, {
                    headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
                });
                const json = await res.json().catch(() => ({}));
                if (!res.ok || json.success === false) throw new Error(json.message || `Could not load staff analytics (${res.status}).`);
                if (Array.isArray(json.data)) {
                    setData(null);
                    setError('The server is on the old analytics code. Replace SmiifyAnalyticsController.js to see this report.');
                } else {
                    setData(json.data);
                }
            } catch (e) {
                if (e.name !== 'AbortError') { setError(e.message); setData(null); }
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        })();
        return () => controller.abort();
    }, [storeId, token, dateFilter, reloadKey]);

    /* ----------------------------- derived ----------------------------- */
    const days = data?.trend?.length || 0;

    // Every active staff appears, even with no sales in this period
    const staffRows = useMemo(() => {
        const rows = [...(data?.staff || [])];
        staffList.forEach((s) => {
            if (!rows.some((r) => r.staffId === s.id || r.name.toLowerCase() === s.name.toLowerCase())) rows.push(zeroStaff(s, days));
        });
        return rows.map((r) => ({ ...r, role: staffList.find((s) => s.id === r.staffId)?.role || '' }));
    }, [data, staffList, days]);

    const totals = data?.totals;
    const unassigned = data?.unassigned;
    const selected = staffRows.find((s) => String(s.id) === String(view)) || null;
    const topStaffGross = Math.max(1, ...staffRows.map((s) => s.gross));
    const membershipRows = staffRows.filter((s) => s.memberships.count > 0).sort((a, b) => b.memberships.amount - a.memberships.amount);
    const packageWorkRows = staffRows.filter((s) => pkgOf(s).count > 0).sort((a, b) => pkgOf(b).value - pkgOf(a).value);
    const packageSellRows = staffRows.filter((s) => (s.packagesSold?.count || 0) > 0).sort((a, b) => b.packagesSold.amount - a.packagesSold.amount);
    const unassignedPct = totals ? pct(totals.unassigned, totals.gross) : 0;
    const cardTotals = totals?.prepaid || { billed: 0, credited: 0, adjust: 0, bills: 0, ratio: 1 };
    const cardRows = staffRows.filter((s) => cardOf(s).billed > 0).sort((a, b) => cardOf(b).billed - cardOf(a).billed);
    const prepaidPolicy = data?.prepaidPolicy || null;
    const note = policyNote(prepaidPolicy, cardTotals.ratio);

    const openBill = useCallback((id) => id && navigate(`/create-bill/${id}/preview`), [navigate]);

    /* ----------------------------- export ----------------------------- */
    const handleExport = () => {
        if (!data) return;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(staffRows.map((s) => ({
            Staff: s.name,
            Bills: s.bills,
            Clients: s.clients,
            'Services (qty)': s.services.count,
            'Services (Rs)': s.services.amount,
            'Products (qty)': s.products.count,
            'Products (Rs)': s.products.amount,
            'Memberships (qty)': s.memberships.count,
            'Memberships (Rs)': s.memberships.amount,
            'Package services (qty)': s.packageServices?.count || 0,
            'Package services credit (Rs)': s.packageServices?.value || 0,
            'Packages sold (Rs)': s.packagesSold?.amount || 0,
            'Prepaid card services billed (Rs)': cardOf(s).billed,
            'Prepaid card services credited (Rs)': cardOf(s).credited,
            'Prepaid card cut (Rs)': cardOf(s).adjust,
            'Staff credit (card at card value + package services)': creditOf(s),
            'Net (excl. GST)': s.net,
            GST: s.gst,
            'Total (incl. GST)': s.gross,
            'Avg bill': s.avgTicket,
            'Share %': s.share,
            Consultations: s.consultations,
        }))), 'Staff summary');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((unassigned?.log || []).map((r) => ({
            'Completed on': fmtDateTime(r.at), Bill: r.invoiceNo, Client: r.client, Item: r.item, Type: r.kind === 'package' ? 'Package service' : KIND_META[r.kind]?.label, Qty: r.qty, Net: r.net, GST: r.gst, Total: r.total, 'Package credit': r.credit || 0,
        }))), 'Without staff');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(staffRows.flatMap((s) => s.membershipSales.map((m) => ({
            Staff: s.name, 'Completed on': fmtDateTime(m.at), Bill: m.invoiceNo, Client: m.client, Membership: m.item, Amount: m.total,
        })))), 'Memberships');
        if (selected) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(selected.log.map((r) => ({
                'Completed on': fmtDateTime(r.at), Bill: r.invoiceNo, Client: r.client, Item: r.item, Type: r.kind === 'package' ? 'Package service' : KIND_META[r.kind]?.label, Qty: r.qty,
                'Shared with': r.shared ? `${r.shared} staff` : '', Net: r.net, GST: r.gst, Total: r.total,
                'Paid by card': r.card ? `${Math.round(r.card.share * 100)}% at ${ratePct(r.card.ratio)}` : '', 'Staff credit': r.credit ?? r.total,
            }))), selected.name.replace(/[\\/?*[\]:]/g, '').slice(0, 28) || 'Staff');
        }
        XLSX.writeFile(wb, `Staff_Analytics_${data.range.startDate}_to_${data.range.endDate}.xlsx`);
    };

    /* ============================== render ============================== */
    if (isLoading && !data) {
        return <div className={styles.state}><Loader2 size={22} className={styles.spin} /><strong>Calculating staff performance...</strong></div>;
    }
    if (error && !data) {
        return (
            <div className={styles.state} role="alert">
                <CircleAlert size={22} className={styles.dangerIcon} />
                <strong>{error}</strong>
                <button type="button" className={styles.btn} onClick={() => setReloadKey((k) => k + 1)}><RefreshCw size={13} /> Try again</button>
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className={styles.page}>
            {/* ---------------- toolbar ---------------- */}
            <div className={styles.toolbar}>
                <div className={styles.tabs} role="tablist" aria-label="Staff analytics view">
                    <button type="button" role="tab" aria-selected={view === 'overview'} className={view === 'overview' ? styles.tabActive : ''} onClick={() => setView('overview')}>
                        <Users size={13} /> All staff
                    </button>
                    <button type="button" role="tab" aria-selected={view === 'unassigned'} className={view === 'unassigned' ? styles.tabActive : ''} onClick={() => setView('unassigned')}>
                        <UserX size={13} /> Without staff
                        {unassigned?.lines > 0 && <em className={styles.tabCount}>{unassigned.lines}</em>}
                    </button>
                </div>
                <div className={styles.toolbarRight}>
                    <label className={styles.staffSelect}>
                        <User size={13} />
                        <select value={selected ? selected.id : ''} onChange={(e) => setView(e.target.value || 'overview')} aria-label="Choose staff">
                            <option value="">Choose staff...</option>
                            {staffRows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </label>
                    {isLoading && <Loader2 size={14} className={styles.spin} aria-label="Updating" />}
                    <button type="button" className={styles.btn} onClick={() => setReloadKey((k) => k + 1)} aria-label="Reload" title="Reload"><RefreshCw size={13} /></button>
                    <button type="button" className={styles.btnPrimary} onClick={handleExport}><Download size={13} /> Export</button>
                </div>
            </div>

            {selected ? (
                <StaffDetail staff={selected} trend={data.trend} totals={totals} onBack={() => setView('overview')} openBill={openBill} />
            ) : view === 'unassigned' ? (
                /* ================= WITHOUT STAFF ================= */
                <>
                    <div className={styles.kpis}>
                        <Kpi icon={UserX} tone="alert" label="Sales without staff" value={inr(unassigned.amount)} sub={`${Math.round(unassignedPct)}% of total sales`} />
                        <Kpi icon={Receipt} label="Bills affected" value={unassigned.bills} sub={`${unassigned.lines} lines with no staff`} />
                        <Kpi icon={IndianRupee} label="Net (excl. GST)" value={inr(unassigned.net)} sub={`GST ${inr(unassigned.gst)}`} />
                    </div>

                    <div className={styles.grid2}>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Most common items without staff</h3></header>
                            {unassigned.topItems.length === 0 ? <p className={styles.empty}>Every line has a staff member.</p> : (
                                <ul className={styles.rankList}>
                                    {unassigned.topItems.map((it) => (
                                        <li key={it.name}>
                                            <span className={styles.rankName}>{it.name}<small>{KIND_META[it.kind]?.label}, {num(it.count)} sold</small></span>
                                            <span className={styles.rankBar}><i style={{ width: `${pct(it.amount, unassigned.topItems[0].amount)}%`, background: KIND_META[it.kind]?.color }} /></span>
                                            <strong>{inr(it.amount)}</strong>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>How to fix</h3></header>
                            <ol className={styles.steps}>
                                <li>Open the bill from the list below.</li>
                                <li>Add the staff member in "Served by" and save.</li>
                                <li>For memberships, choose "Sold by" on the membership desk.</li>
                            </ol>
                            <p className={styles.note}>Until then these sales are not counted in anyone's performance or commission.</p>
                        </section>
                    </div>

                    <LogTable title="Lines without staff" rows={unassigned.log} openBill={openBill} emptyText="No sales without staff in this period." />
                </>
            ) : (
                /* ================= OVERVIEW ================= */
                <>
                    <div className={styles.kpis}>
                        <Kpi icon={IndianRupee} label="Total sales" value={inr(totals.gross)} sub={`Net ${inr(totals.net)}, GST ${inr(totals.gst)}`} />
                        <Kpi
                            icon={Users}
                            label="Credited to staff"
                            value={inr(totals.credit ?? totals.assigned)}
                            sub={cardTotals.adjust > 0 ? `Card cut −${shortInr(cardTotals.adjust)}` : `${Math.round(pct(totals.assigned, totals.gross))}% of sales`}
                        />
                        <Kpi
                            icon={UserX}
                            tone={totals.unassigned > 0 ? 'alert' : undefined}
                            label="Without staff"
                            value={inr(totals.unassigned)}
                            sub={totals.unassigned > 0 ? 'Click to see the bills' : 'All lines credited'}
                            onClick={totals.unassigned > 0 ? () => setView('unassigned') : undefined}
                        />
                        <Kpi icon={Crown} label="Memberships sold" value={inr(totals.memberships.amount)} sub={`${num(totals.memberships.count)} sold`} />
                        {(totals.packagesSold?.count || 0) > 0 && (
                            <Kpi icon={PackageOpen} label="Packages sold" value={inr(totals.packagesSold.amount)} sub={`${num(totals.packagesSold.count)} sold`} />
                        )}
                        {(totals.packageServices?.count || 0) > 0 && (
                            <Kpi icon={Crown} label="Package services done" value={inr(totals.packageServices.value)} sub={`${num(totals.packageServices.count)} done (paid earlier)`} />
                        )}
                        {cardTotals.billed > 0 && (
                            <Kpi
                                icon={CreditCard}
                                label="Prepaid card services"
                                value={inr(cardTotals.credited)}
                                sub={prepaidPolicy && !prepaidPolicy.active ? `Full price · ${shortInr(cardTotals.billed)}` : `Billed ${shortInr(cardTotals.billed)} · at ${ratePct(cardTotals.ratio)}`}
                            />
                        )}
                        <Kpi icon={Receipt} label="Bills" value={totals.bills} sub={`${totals.clients} clients`} />
                        <Kpi icon={TrendingUp} label="Average bill" value={inr(totals.avgBill)} sub={`${num(totals.services.count)} services done`} />
                    </div>

                    {unassignedPct >= 10 && (
                        <button type="button" className={styles.alert} onClick={() => setView('unassigned')}>
                            <AlertCircle size={14} />
                            <span><strong>{Math.round(unassignedPct)}% of sales ({inr(totals.unassigned)})</strong> have no staff attached, so staff reports and commission will look lower than actual.</span>
                            <em>View bills</em>
                        </button>
                    )}

                    <div className={styles.grid2Wide}>
                        <section className={styles.card}>
                            <header className={styles.cardHead}>
                                <h3>Daily sales</h3>
                                <small>{fmtDay(data.range.startDate)} to {fmtDay(data.range.endDate)}</small>
                            </header>
                            <TrendChart
                                points={data.trend}
                                series={[
                                    { key: 'assigned', label: 'Credited to staff', color: COLOR_STAFF },
                                    { key: 'unassigned', label: 'Without staff', color: COLOR_NO_STAFF },
                                ]}
                            />
                        </section>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Sales mix</h3></header>
                            <Donut
                                parts={KINDS.map((k) => ({ label: KIND_META[k].label, value: totals[k].amount, color: KIND_META[k].color }))}
                                centerLabel="Total"
                                centerValue={shortInr(totals.gross)}
                            />
                        </section>
                    </div>

                    <div className={styles.grid2Wide}>
                        <section className={styles.card}>
                            <header className={styles.cardHead}>
                                <h3>Leaderboard <small style={{ fontWeight: 700, fontSize: 11, color: 'var(--h-color-neutral-500, #797980)' }}>by staff credit</small></h3>
                                <div className={styles.legend}>{KINDS.map((k) => <span key={k}><i style={{ background: KIND_META[k].color }} /> {KIND_META[k].label}</span>)}</div>
                            </header>
                            {staffRows.length === 0 ? <p className={styles.empty}>No staff found.</p> : (
                                <ul className={styles.board}>
                                    {staffRows.map((s, i) => (
                                        <li key={s.id}>
                                            <button type="button" onClick={() => setView(String(s.id))} className={styles.boardRow}>
                                                <span className={`${styles.rank} ${i === 0 && s.gross > 0 ? styles.rankTop : ''}`}>
                                                    {i === 0 && s.gross > 0 ? <Award size={12} /> : i + 1}
                                                </span>
                                                <span className={styles.avatar}>{initials(s.name)}</span>
                                                <span className={styles.boardName}>{s.name}<small>{s.bills} bills · {Math.round(s.creditShare ?? s.share)}%{cardOf(s).adjust > 0 ? ` · card −${shortInr(cardOf(s).adjust)}` : ''}</small></span>
                                                <span className={styles.boardBar}>
                                                    {KINDS.map((k) => (s[k].amount > 0 ? (
                                                        <i key={k} title={`${KIND_META[k].label}: ${inr(s[k].amount)}`} style={{ width: `${pct(s[k].amount, topStaffGross)}%`, background: KIND_META[k].color }} />
                                                    ) : null))}
                                                </span>
                                                <strong title={`Billed ${inr(s.gross)}`}>{inr(creditOf(s))}</strong>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Memberships by staff</h3><small>{inr(totals.memberships.amount)}</small></header>
                            {membershipRows.length === 0 ? (
                                <p className={styles.empty}>No memberships credited to staff yet. Use "Sold by" on the membership desk.</p>
                            ) : (
                                <ul className={styles.rankList}>
                                    {membershipRows.map((s) => (
                                        <li key={s.id}>
                                            <span className={styles.rankName}>{s.name}<small>{num(s.memberships.count)} sold</small></span>
                                            <span className={styles.rankBar}><i style={{ width: `${pct(s.memberships.amount, membershipRows[0].memberships.amount)}%`, background: KIND_META.memberships.color }} /></span>
                                            <strong>{inr(s.memberships.amount)}</strong>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>

                    {(packageWorkRows.length > 0 || packageSellRows.length > 0) && (
                        <div className={styles.grid2}>
                            <section className={styles.card}>
                                <header className={styles.cardHead}>
                                    <h3>Package services by staff</h3>
                                    <small title="Services given from client packages. Credit = the service's share of the package price.">{inr(totals.packageServices?.value || 0)} credit</small>
                                </header>
                                {packageWorkRows.length === 0 ? <p className={styles.empty}>No package services given in this period.</p> : (
                                    <ul className={styles.rankList}>
                                        {packageWorkRows.map((s) => (
                                            <li key={s.id}>
                                                <span className={styles.rankName}>{s.name}<small>{num(pkgOf(s).count)} done</small></span>
                                                <span className={styles.rankBar}><i style={{ width: `${pct(pkgOf(s).value, pkgOf(packageWorkRows[0]).value)}%`, background: PKG_COLOR }} /></span>
                                                <strong>{inr(pkgOf(s).value)}</strong>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                            <section className={styles.card}>
                                <header className={styles.cardHead}><h3>Packages sold by staff</h3><small>{inr(totals.packagesSold?.amount || 0)}</small></header>
                                {packageSellRows.length === 0 ? <p className={styles.empty}>No packages sold in this period.</p> : (
                                    <ul className={styles.rankList}>
                                        {packageSellRows.map((s) => (
                                            <li key={s.id}>
                                                <span className={styles.rankName}>{s.name}<small>{num(s.packagesSold.count)} sold</small></span>
                                                <span className={styles.rankBar}><i style={{ width: `${pct(s.packagesSold.amount, packageSellRows[0].packagesSold.amount)}%`, background: KIND_META.memberships.color }} /></span>
                                                <strong>{inr(s.packagesSold.amount)}</strong>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        </div>
                    )}

                    {cardTotals.billed > 0 && (
                        <section className={styles.card}>
                            <header className={styles.cardHead}>
                                <h3><CreditCard size={14} style={{ verticalAlign: '-2px', marginRight: 6, color: CARD_COLOR }} />Prepaid card services</h3>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <small>{inr(cardTotals.billed)} billed, {inr(cardTotals.credited)} credited{cardTotals.adjust > 0 ? `, −${inr(cardTotals.adjust)}` : ''}</small>
                                    <button type="button" className={styles.btn} onClick={() => navigate(SETTINGS_PATH)} title="Settings > Staff defaults > Prepaid card services">
                                        <Settings2 size={13} /> Change in Settings
                                    </button>
                                </span>
                            </header>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', margin: '0 0 12px', borderRadius: 10, background: 'var(--h-color-primary-50, #f5f6ff)', fontSize: 12, fontWeight: 600, lineHeight: 1.5, color: 'var(--h-color-neutral-700, #36363b)' }}>
                                <Info size={15} style={{ flexShrink: 0, marginTop: 2, color: 'var(--h-color-primary-600, #673de6)' }} />
                                <span>
                                    <b style={{ display: 'block', marginBottom: 2 }}>{note.title}</b>
                                    {note.text} Bills and sales totals are never changed.
                                </span>
                            </div>
                            <ul className={styles.rankList}>
                                {cardRows.map((s) => (
                                    <li key={s.id}>
                                        <span className={styles.rankName}>{s.name}<small>Billed {inr(cardOf(s).billed)} on cards, at {ratePct(cardOf(s).ratio)}</small></span>
                                        <span className={styles.rankBar}>
                                            <i style={{ width: `${pct(cardOf(s).credited, cardRows[0] ? cardOf(cardRows[0]).billed : 1)}%`, background: CARD_COLOR }} />
                                        </span>
                                        <strong title={cardOf(s).adjust > 0 ? `Cut: ${inr(cardOf(s).adjust)}` : 'Counted at full price'}>
                                            {inr(cardOf(s).credited)}
                                            {cardOf(s).adjust > 0 && <small style={{ display: 'block', fontSize: 10.5, fontWeight: 700, color: 'var(--h-color-danger-600, #be1025)' }}>−{inr(cardOf(s).adjust)}</small>}
                                        </strong>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* ---------------- staff table ---------------- */}
                    <section className={styles.card}>
                        <header className={styles.cardHead}><h3>Staff summary</h3><small>Click a row for full details</small></header>
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Staff</th>
                                        <th className={styles.r}>Bills</th>
                                        <th className={styles.r}>Clients</th>
                                        <th className={styles.r}>Services</th>
                                        <th className={styles.r}>Products</th>
                                        <th className={styles.r}>Memberships</th>
                                        <th className={styles.r} title="Services given from client packages. Credit = the service's share of the package price (not shop revenue).">Package services</th>
                                        <th className={styles.r}>Net</th>
                                        <th className={styles.r}>GST</th>
                                        <th className={styles.r}>Total billed</th>
                                        <th className={styles.r} title="Services paid by prepaid card are credited at card value (card price ÷ card credit). This is the amount taken off.">Card cut</th>
                                        <th className={styles.r} title="What counts for this staff: sales (card services at card value) + package services.">Staff credit</th>
                                        <th className={styles.r}>Avg bill</th>
                                        <th className={styles.r}>Share</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {staffRows.map((s) => (
                                        <tr key={s.id} onClick={() => setView(String(s.id))} className={styles.clickRow}>
                                            <td>
                                                <span className={styles.person}>
                                                    <span className={styles.avatar}>{initials(s.name)}</span>
                                                    <span><strong>{s.name}</strong>{s.role && <small>{s.role}</small>}</span>
                                                </span>
                                            </td>
                                            <td className={styles.r}>{s.bills}</td>
                                            <td className={styles.r}>{s.clients}</td>
                                            <td className={styles.r}>{inr(s.services.amount)}<small>{num(s.services.count)} done</small></td>
                                            <td className={styles.r}>{inr(s.products.amount)}<small>{num(s.products.count)} sold</small></td>
                                            <td className={styles.r}>{inr(s.memberships.amount)}<small>{num(s.memberships.count)} sold</small></td>
                                            <td className={styles.r}>{inr(s.packageServices?.value || 0)}<small>{num(s.packageServices?.count || 0)} done</small></td>
                                            <td className={styles.r}>{inr(s.net)}</td>
                                            <td className={styles.r}>{inr(s.gst)}</td>
                                            <td className={styles.r}>{inr(s.gross)}</td>
                                            <td className={styles.r} style={cardOf(s).adjust > 0 ? { color: 'var(--h-color-danger-600, #be1025)' } : undefined}>
                                                {cardOf(s).adjust > 0 ? `−${inr(cardOf(s).adjust)}` : '—'}
                                                {cardOf(s).billed > 0 && <small>{inr(cardOf(s).billed)} on card</small>}
                                            </td>
                                            <td className={`${styles.r} ${styles.strong}`}>{inr(creditOf(s))}</td>
                                            <td className={styles.r}>{inr(s.avgTicket)}</td>
                                            <td className={styles.r}><span className={styles.sharePill}>{Math.round(s.creditShare ?? s.share)}%</span></td>
                                        </tr>
                                    ))}
                                    {unassigned.amount > 0 && (
                                        <tr className={`${styles.clickRow} ${styles.alertRow}`} onClick={() => setView('unassigned')}>
                                            <td>
                                                <span className={styles.person}>
                                                    <span className={`${styles.avatar} ${styles.avatarMuted}`}><UserX size={12} /></span>
                                                    <span><strong>Without staff</strong><small>Not credited</small></span>
                                                </span>
                                            </td>
                                            <td className={styles.r}>{unassigned.bills}</td>
                                            <td className={styles.r}>—</td>
                                            <td className={styles.r} colSpan={4}>{unassigned.lines} lines</td>
                                            <td className={styles.r}>{inr(unassigned.net)}</td>
                                            <td className={styles.r}>{inr(unassigned.gst)}</td>
                                            <td className={styles.r}>{inr(unassigned.amount)}</td>
                                            <td className={styles.r}>—</td>
                                            <td className={`${styles.r} ${styles.strong}`}>Not credited</td>
                                            <td className={styles.r}>—</td>
                                            <td className={styles.r}><span className={`${styles.sharePill} ${styles.sharePillMuted}`}>{Math.round(unassignedPct)}%</span></td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td>Total</td>
                                        <td className={styles.r}>{totals.bills}</td>
                                        <td className={styles.r}>{totals.clients}</td>
                                        <td className={styles.r}>{inr(totals.services.amount)}</td>
                                        <td className={styles.r}>{inr(totals.products.amount)}</td>
                                        <td className={styles.r}>{inr(totals.memberships.amount)}</td>
                                        <td className={styles.r}>{inr(totals.packageServices?.value || 0)}</td>
                                        <td className={styles.r}>{inr(totals.net)}</td>
                                        <td className={styles.r}>{inr(totals.gst)}</td>
                                        <td className={styles.r}>{inr(totals.gross)}</td>
                                        <td className={styles.r}>{cardTotals.adjust > 0 ? `−${inr(cardTotals.adjust)}` : '—'}</td>
                                        <td className={styles.r}>{inr(totals.credit ?? totals.gross)}</td>
                                        <td className={styles.r}>{inr(totals.avgBill)}</td>
                                        <td className={styles.r}>100%</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        <p className={styles.footNote}>A line served by two staff is split equally. Bill discounts are spread across lines, so totals match the bills. {prepaidPolicy && !prepaidPolicy.active ? 'Prepaid card services count at the full bill price (see Settings > Staff defaults).' : 'Services paid by prepaid card count at card value in Staff credit; Total billed keeps the bill price.'}</p>
                    </section>
                </>
            )}
        </div>
    );
};

/* ============================== pieces ============================== */
const Kpi = ({ icon: Icon, label, value, sub, tone, onClick }) => {
    const Tag = onClick ? 'button' : 'div';
    return (
        <Tag
            type={onClick ? 'button' : undefined}
            className={`${styles.kpi} ${tone === 'alert' ? styles.kpiAlert : ''} ${onClick ? styles.kpiClickable : ''}`}
            onClick={onClick}
        >
            <span className={styles.kpiIcon}><Icon size={15} /></span>
            <span className={styles.kpiText}>
                <small>{label}</small>
                <strong>{value}</strong>
                {sub && <em>{sub}</em>}
            </span>
        </Tag>
    );
};

const LogTable = ({ title, rows, openBill, emptyText, showShared = false }) => (
    <section className={styles.card}>
        <header className={styles.cardHead}><h3>{title}</h3><small>{rows.length} lines</small></header>
        <div className={`${styles.tableWrap} ${styles.tableTall}`}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Completed on</th>
                        <th>Bill</th>
                        <th>Client</th>
                        <th>Item</th>
                        <th>Type</th>
                        <th className={styles.r}>Qty</th>
                        <th className={styles.r}>Net</th>
                        <th className={styles.r}>GST</th>
                        <th className={styles.r}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={9} className={styles.emptyCell}>{emptyText}</td></tr>
                    ) : rows.map((r, i) => (
                        <tr key={`${r.invoiceId}-${i}`}>
                            <td className={styles.muted}>{fmtDateTime(r.at)}</td>
                            <td><button type="button" className={styles.link} onClick={() => openBill(r.invoiceId)}>#{r.invoiceNo}</button></td>
                            <td>{r.client}</td>
                            <td>
                                {r.item}
                                {showShared && r.shared > 0 && <small className={styles.sharedTag}>Shared with {r.shared - 1} more</small>}
                            </td>
                            <td>
                                {r.kind === 'package'
                                    ? <span className={styles.kindTag} style={{ '--k': 'var(--h-color-success-600, #007a49)' }} title={r.package ? `From package: ${r.package}` : 'From a client package'}>Package</span>
                                    : <span className={styles.kindTag} style={{ '--k': KIND_META[r.kind]?.color }}>{KIND_META[r.kind]?.label}</span>}
                                {r.card && <span className={styles.kindTag} style={{ '--k': CARD_COLOR, marginLeft: 4 }} title="Paid by prepaid card">Card</span>}
                            </td>
                            <td className={styles.r}>{num(r.qty)}</td>
                            <td className={styles.r}>{inr2(r.net)}</td>
                            <td className={styles.r}>{inr2(r.gst)}</td>
                            <td className={`${styles.r} ${styles.strong}`}>
                                {r.kind === 'package' ? (
                                    <span title="Paid earlier in the package. Staff credit at the package price share.">{inr2(r.credit || 0)}<small> credit</small></span>
                                ) : r.card ? (
                                    <span title={`${Math.round(r.card.share * 100)}% of this line was paid by prepaid card, credited at ${ratePct(r.card.ratio)} of its price.`}>
                                        {inr2(r.total)}
                                        <small style={{ display: 'block', color: CARD_COLOR, fontWeight: 800 }}>credit {inr2(r.credit ?? r.total)} · card {ratePct(r.card.ratio)}</small>
                                    </span>
                                ) : inr2(r.total)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </section>
);

const StaffDetail = ({ staff, trend, totals, onBack, openBill }) => {
    const points = trend.map((p, i) => ({ date: p.date, sales: staff.trend[i] || 0 }));
    const best = points.reduce((b, p) => (p.sales > b.sales ? p : b), { sales: 0 });
    const activeDays = points.filter((p) => p.sales > 0).length;

    return (
        <>
            <div className={styles.profile}>
                <button type="button" className={styles.btn} onClick={onBack} aria-label="Back to all staff"><ArrowLeft size={13} /></button>
                <span className={`${styles.avatar} ${styles.avatarLg}`}>{initials(staff.name)}</span>
                <div className={styles.profileText}>
                    <h2>{staff.name}</h2>
                    <span>{staff.role || 'Staff'}, {Math.round(staff.creditShare ?? staff.share)}% of store staff credit{cardOf(staff).adjust > 0 ? `, billed ${inr(staff.gross)}` : ''}</span>
                </div>
            </div>

            <div className={styles.kpis}>
                <Kpi
                    icon={IndianRupee}
                    label="Staff credit"
                    value={inr(creditOf(staff))}
                    sub={[
                        cardOf(staff).adjust > 0 ? `card −${shortInr(cardOf(staff).adjust)}` : '',
                        pkgOf(staff).value > 0 ? `pkg +${shortInr(pkgOf(staff).value)}` : '',
                    ].filter(Boolean).join(' · ') || `Billed ${inr(staff.gross)}`}
                />
                {cardOf(staff).billed > 0 && (
                    <Kpi icon={CreditCard} label="Prepaid card services" value={inr(cardOf(staff).credited)} sub={`Billed ${shortInr(cardOf(staff).billed)} · at ${ratePct(cardOf(staff).ratio)}`} />
                )}
                <Kpi icon={Scissors} label="Services" value={inr(staff.services.amount)} sub={`${num(staff.services.count)} done`} />
                <Kpi icon={PackageOpen} label="Package services" value={inr(pkgOf(staff).value)} sub={`${num(pkgOf(staff).count)} done, paid earlier`} />
                <Kpi icon={Package} label="Products" value={inr(staff.products.amount)} sub={`${num(staff.products.count)} sold`} />
                <Kpi icon={Crown} label="Memberships" value={inr(staff.memberships.amount)} sub={`${num(staff.memberships.count)} sold`} />
                <Kpi icon={Receipt} label="Bills / clients" value={`${staff.bills} / ${staff.clients}`} sub={`Avg bill ${inr(staff.avgTicket)}`} />
                <Kpi icon={Users} label="Consultations" value={staff.consultations} sub={`${activeDays} active days`} />
            </div>

            <div className={styles.grid2Wide}>
                <section className={styles.card}>
                    <header className={styles.cardHead}>
                        <h3>{pkgOf(staff).value > 0 ? 'Daily sales and package credit' : 'Daily sales'}</h3>
                        {best.sales > 0 && <small>Best day: {fmtDay(best.date)}, {inr(best.sales)}</small>}
                    </header>
                    <TrendChart points={points} series={[{ key: 'sales', label: staff.name, color: COLOR_STAFF }]} />
                </section>
                <section className={styles.card}>
                    <header className={styles.cardHead}><h3>Work mix</h3></header>
                    <Donut
                        parts={[
                            ...KINDS.map((k) => ({ label: KIND_META[k].label, value: staff[k].amount, color: KIND_META[k].color })),
                            { label: 'Package services', value: pkgOf(staff).value, color: PKG_COLOR },
                        ]}
                        centerLabel="of store"
                        centerValue={`${Math.round(pct(creditOf(staff), totals.credit ?? totals.gross))}%`}
                    />
                </section>
            </div>

            <div className={styles.grid2}>
                <section className={styles.card}>
                    <header className={styles.cardHead}><h3>Top services</h3></header>
                    {staff.topServices.length === 0 ? <p className={styles.empty}>No services in this period.</p> : (
                        <ul className={styles.rankList}>
                            {staff.topServices.map((t) => (
                                <li key={t.name}>
                                    <span className={styles.rankName}>{t.name}</span>
                                    <span className={styles.rankBar}><i style={{ width: `${pct(t.count, staff.topServices[0].count)}%`, background: KIND_META.services.color }} /></span>
                                    <strong>{num(t.count)}x</strong>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
                <section className={styles.card}>
                    <header className={styles.cardHead}><h3>Memberships and packages sold</h3><small>{inr(staff.memberships.amount)}</small></header>
                    {staff.membershipSales.length === 0 ? <p className={styles.empty}>No membership or package sales in this period.</p> : (
                        <ul className={styles.saleList}>
                            {staff.membershipSales.slice(0, 8).map((m, i) => (
                                <li key={`${m.invoiceId}-${i}`}>
                                    <span><strong>{m.item}{m.isPackage && <em style={{ marginLeft: 6, fontStyle: 'normal', fontSize: 10, fontWeight: 800, color: PKG_COLOR }}>PACKAGE</em>}</strong><small>{m.client}, {fmtDateTime(m.at)}</small></span>
                                    <button type="button" className={styles.link} onClick={() => openBill(m.invoiceId)}>{inr(m.total)}</button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            {pkgOf(staff).count > 0 && (
                <section className={styles.card}>
                    <header className={styles.cardHead}>
                        <h3>Package services given</h3>
                        <small title="Paid by the client earlier when the package was bought. Credit = the service's share of the package price.">{num(pkgOf(staff).count)} done, {inr(pkgOf(staff).value)} credit</small>
                    </header>
                    <ul className={styles.rankList}>
                        {(pkgOf(staff).items || []).map((x) => (
                            <li key={`${x.name}|${x.package}`}>
                                <span className={styles.rankName}>{x.name}<small>{x.package ? `From ${x.package}` : 'From a client package'}, {num(x.count)}x</small></span>
                                <span className={styles.rankBar}><i style={{ width: `${pct(x.value, pkgOf(staff).items[0].value)}%`, background: PKG_COLOR }} /></span>
                                <strong>{inr(x.value)}</strong>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <LogTable title={`Work log: ${staff.name}`} rows={staff.log} openBill={openBill} emptyText={`No work logged by ${staff.name} in this period.`} showShared />
        </>
    );
};

export default StaffAnalytics;
